<script setup>
import { computed, ref } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  products: {
    type: Object,
    default: () => ({}),
  },
  appointments: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const activeTab = ref('products');

function formatCurrency(value) {
  if (!value) return 'R$ 0';
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getStatusColor(status) {
  const colors = {
    scheduled: 'bg-n-blue-3 text-n-blue-11',
    confirmed: 'bg-n-teal-3 text-n-teal-11',
    completed: 'bg-n-violet-3 text-n-violet-11',
    cancelled: 'bg-n-ruby-3 text-n-ruby-11',
    no_show: 'bg-n-amber-3 text-n-amber-11',
  };
  return colors[status] || 'bg-n-slate-3 text-n-slate-11';
}

function getStatusLabel(status) {
  const labels = {
    scheduled: 'Agendado',
    confirmed: 'Confirmado',
    completed: 'Concluido',
    cancelled: 'Cancelado',
    no_show: 'Nao compareceu',
  };
  return labels[status] || status;
}

const topProducts = computed(() => props.products?.top_products || []);
const upcomingAppointments = computed(() => props.appointments?.upcoming || []);
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex gap-1">
        <button
          class="px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-2"
          :class="
            activeTab === 'products'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'products'"
        >
          <span class="i-lucide-package" />
          Produtos
        </button>
        <button
          class="px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-2"
          :class="
            activeTab === 'appointments'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'appointments'"
        >
          <span class="i-lucide-calendar" />
          Agendamentos
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Products Tab -->
    <div v-else-if="activeTab === 'products'" class="flex flex-col gap-4">
      <!-- Summary -->
      <div class="grid grid-cols-3 gap-3">
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Total Produtos</div>
          <div class="text-xl font-bold text-n-slate-12">
            {{ formatNumber(products.total_products) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Valor Total</div>
          <div class="text-xl font-bold text-n-teal-11">
            {{ formatCurrency(products.total_value) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Ticket Medio</div>
          <div class="text-xl font-bold text-n-violet-11">
            {{ formatCurrency(products.avg_value) }}
          </div>
        </div>
      </div>

      <!-- Top Products -->
      <div v-if="topProducts.length > 0" class="flex flex-col gap-2">
        <div class="text-xs font-medium text-n-slate-11">
          Produtos Mais Vendidos
        </div>
        <div
          v-for="product in topProducts"
          :key="product.name"
          class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
        >
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">{{
              product.name
            }}</span>
            <span class="text-xs text-n-slate-11">{{ formatNumber(product.quantity) }} unidades</span>
          </div>
          <span class="text-sm font-bold text-n-teal-11">{{
            formatCurrency(product.total_value)
          }}</span>
        </div>
      </div>

      <!-- By Currency -->
      <div
        v-if="
          products.by_currency && Object.keys(products.by_currency).length > 0
        "
        class="flex flex-col gap-2"
      >
        <div class="text-xs font-medium text-n-slate-11">Por Moeda</div>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="(value, currency) in products.by_currency"
            :key="currency"
            class="px-3 py-2 rounded-lg bg-n-alpha-1"
          >
            <span class="text-xs text-n-slate-10">{{ currency }}</span>
            <div class="text-sm font-bold text-n-slate-12">
              {{ formatNumber(value) }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="topProducts.length === 0"
        class="text-center text-n-slate-11 py-4"
      >
        Nenhum produto registrado
      </div>
    </div>

    <!-- Appointments Tab -->
    <div v-else-if="activeTab === 'appointments'" class="flex flex-col gap-4">
      <!-- Summary -->
      <div class="grid grid-cols-4 gap-3">
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Total</div>
          <div class="text-xl font-bold text-n-slate-12">
            {{ formatNumber(appointments.total) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Hoje</div>
          <div class="text-xl font-bold text-n-amber-11">
            {{ formatNumber(appointments.today) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Esta Semana</div>
          <div class="text-xl font-bold text-n-blue-11">
            {{ formatNumber(appointments.this_week) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Concluidos</div>
          <div class="text-xl font-bold text-n-teal-11">
            {{ formatNumber(appointments.completed) }}
          </div>
        </div>
      </div>

      <!-- By Status -->
      <div
        v-if="
          appointments.by_status &&
          Object.keys(appointments.by_status).length > 0
        "
        class="flex flex-wrap gap-2"
      >
        <div
          v-for="(count, status) in appointments.by_status"
          :key="status"
          class="px-3 py-1.5 rounded-lg text-xs font-medium"
          :class="getStatusColor(status)"
        >
          {{ getStatusLabel(status) }}: {{ formatNumber(count) }}
        </div>
      </div>

      <!-- Upcoming -->
      <div v-if="upcomingAppointments.length > 0" class="flex flex-col gap-2">
        <div class="text-xs font-medium text-n-slate-11">
          Proximos Agendamentos
        </div>
        <div
          v-for="appointment in upcomingAppointments"
          :key="appointment.id"
          class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
        >
          <div class="flex items-center gap-3">
            <span class="i-lucide-calendar-clock text-n-amber-10" />
            <div class="flex flex-col">
              <span class="text-sm font-medium text-n-slate-12">{{
                appointment.title || 'Agendamento'
              }}</span>
              <span class="text-xs text-n-slate-11">{{
                appointment.contact_name
              }}</span>
            </div>
          </div>
          <div class="flex flex-col items-end">
            <span class="text-xs font-medium text-n-slate-12">{{
              formatDate(appointment.scheduled_at)
            }}</span>
            <span
              class="text-xs px-2 py-0.5 rounded"
              :class="getStatusColor(appointment.status)"
            >
              {{ getStatusLabel(appointment.status) }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="upcomingAppointments.length === 0 && !appointments.total"
        class="text-center text-n-slate-11 py-4"
      >
        Nenhum agendamento registrado
      </div>
    </div>
  </div>
</template>
